import React, { useState, useEffect } from 'react';
import {
  getDashboardStats,
  getDashboardDistribution,
  triggerRetrain,
} from '../utils/api';
import StatsCard from '../components/StatsCard';
import ModelCard from '../components/ModelCard';
import DistributionBar from '../components/DistributionBar';
import TestPredictor from '../components/TestPredictor';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retraining, setRetraining] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, distData] = await Promise.all([
        getDashboardStats(),
        getDashboardDistribution(),
      ]);

      if (statsData.success) {
        setStats(statsData.data);
      }

      if (distData.success) {
        setDistribution(distData.data);
      }

      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // 1분마다 자동 새로고침
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleRetrain = async () => {
    if (!window.confirm('수동 재학습을 시작하시겠습니까? (시간이 오래 걸릴 수 있습니다)')) {
      return;
    }

    try {
      setRetraining(true);
      const result = await triggerRetrain();

      if (result.success) {
        alert('재학습 완료!');
        fetchData(); // 데이터 새로고침
      } else {
        alert('재학습 실패: ' + result.error);
      }
    } catch (err) {
      alert('재학습 오류: ' + err.message);
    } finally {
      setRetraining(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-4">오류 발생</h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  const totalTemp =
    distribution?.temperature
      ? Object.values(distribution.temperature).reduce((a, b) => a + b, 0)
      : 0;

  const totalSeason =
    distribution?.season
      ? Object.values(distribution.season).reduce((a, b) => a + b, 0)
      : 0;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 헤더 */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Personal Color ML Dashboard
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                마지막 업데이트: {lastUpdate.toLocaleString('ko-KR')}
              </p>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center"
            >
              <span className="mr-2">🔄</span>
              {loading ? '새로고침 중...' : '새로고침'}
            </button>
          </div>
        </div>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* 모델 현황 */}
        <StatsCard title="모델 현황" icon="📈" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-600">총 학습 데이터</div>
              <div className="text-2xl font-bold text-blue-600">
                {stats?.total_data_count || 0}개
              </div>
            </div>
            <div>
              <div className="text-gray-600">현재 MongoDB 데이터</div>
              <div className="text-2xl font-bold text-green-600">
                {stats?.current_mongodb_count || 0}개
              </div>
            </div>
            <div>
              <div className="text-gray-600">신규 데이터</div>
              <div className="text-2xl font-bold text-orange-600">
                +{stats?.new_data_count || 0}개
              </div>
            </div>
            <div>
              <div className="text-gray-600">마지막 학습</div>
              <div className="text-lg font-semibold text-gray-700">
                {stats?.last_update
                  ? new Date(stats.last_update).toLocaleDateString('ko-KR')
                  : 'N/A'}
              </div>
            </div>
          </div>
        </StatsCard>

        {/* 성능 지표 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {stats?.models?.temperature && (
            <ModelCard
              title="온도감"
              icon="🌡️"
              modelName={stats.models.temperature.model_name}
              accuracy={stats.models.temperature.accuracy}
              dataCount={stats.models.temperature.data_count}
            />
          )}
          {stats?.models?.season && (
            <ModelCard
              title="계절"
              icon="🍂"
              modelName={stats.models.season.model_name}
              accuracy={stats.models.season.accuracy}
              dataCount={stats.models.season.data_count}
            />
          )}
          {stats?.models?.ai_diagnosis && (
            <ModelCard
              title="세부 진단"
              icon="🎨"
              modelName={stats.models.ai_diagnosis.model_name}
              accuracy={stats.models.ai_diagnosis.accuracy}
              dataCount={stats.models.ai_diagnosis.data_count}
            />
          )}
        </div>

        {/* 데이터 분포 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* 온도감 분포 */}
          <StatsCard title="온도감 분포" icon="🌡️">
            {distribution?.temperature && (
              <div>
                <DistributionBar
                  label="Warm"
                  value={distribution.temperature.warm || 0}
                  total={totalTemp}
                  color="bg-red-500"
                />
                <DistributionBar
                  label="Neutral"
                  value={distribution.temperature.neutral || 0}
                  total={totalTemp}
                  color="bg-gray-500"
                />
                <DistributionBar
                  label="Cool"
                  value={distribution.temperature.cool || 0}
                  total={totalTemp}
                  color="bg-blue-500"
                />
                <div className="mt-3 text-sm text-gray-600 text-right">
                  총 {totalTemp}개
                </div>
              </div>
            )}
          </StatsCard>

          {/* 계절 분포 */}
          <StatsCard title="계절 분포" icon="🍂">
            {distribution?.season && (
              <div>
                <DistributionBar
                  label="Spring"
                  value={distribution.season.spring || 0}
                  total={totalSeason}
                  color="bg-pink-500"
                />
                <DistributionBar
                  label="Summer"
                  value={distribution.season.summer || 0}
                  total={totalSeason}
                  color="bg-blue-400"
                />
                <DistributionBar
                  label="Autumn"
                  value={distribution.season.autumn || 0}
                  total={totalSeason}
                  color="bg-orange-500"
                />
                <DistributionBar
                  label="Winter"
                  value={distribution.season.winter || 0}
                  total={totalSeason}
                  color="bg-cyan-600"
                />
                <div className="mt-3 text-sm text-gray-600 text-right">
                  총 {totalSeason}개
                </div>
              </div>
            )}
          </StatsCard>
        </div>

        {/* 테스트 예측 */}
        <div className="mb-6">
          <TestPredictor />
        </div>

        {/* 관리자 기능 */}
        <StatsCard title="관리자 기능" icon="⚙️">
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleRetrain}
              disabled={retraining}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {retraining ? '재학습 중...' : '수동 재학습 실행'}
            </button>
            <button
              onClick={fetchData}
              className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              데이터 새로고침
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            자동 재학습은 매일 새벽 2시에 실행됩니다. (신규 데이터 50개 이상 시)
          </p>
        </StatsCard>
      </main>

      {/* 푸터 */}
      <footer className="bg-white shadow-md mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 text-sm">
            Personal Color ML System v1.0.0 | Powered by Flask & React
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
